/* eslint-disable max-len */
import {useState} from 'react';
import {NodeViewRendererProps, useTransform} from '@baidu/realtime-editor';
import {
    Block,
    Link,
    Paragraph,
    Heading,
    OrderedList,
    Text,
    Colors,
} from '@baidu/morpho-json-builder';
import {RangePicker} from '@baidu/luffy-comp-date-picker';
import dayjs from 'dayjs';

import Input from '@/components/Input';
import Button from '@/components/Button';
import Loading from '@/components/Loading';
import toast, {ToastType} from '@/components/Toast';
import sdk from '@/sdk';

import {IErrorLocate} from '..';
import {mockRequest} from './mock';
import styles from './index.module.scss';


const DefaultWeirwoodURL = 'https://weirwood.baidu-int.com/platform/app/135/home';
const ErrorTypeList = ['js', 'resource', 'api'];

enum ErrorType {
    JS = 'js',
    RESOURCE = 'resource',
    API = 'api',
}

interface IQueryUrlItem {
    url: string;
    type: ErrorType;
}

interface IErrorMsg {
    type: ErrorType;
    msg: string;
}

interface IErrorItem {
    userId: string;
    docId: string;
    type: ErrorType;
    content: string;
    time: number;
}

interface IContent {
    queryUrls: IQueryUrlItem[];
    errorList: IErrorItem[];
    errorMsgs: IErrorMsg[];
    userAgentScope: string[];
    filter: {
        docId: string;
        userId: string;
        startDate: number;
        endDate: number;
    };
}

function formatTime(time: string | number | Date, format = 'MM/DD HH:mm:ss') {
    return dayjs(time).format(format);
}

function createErrorContent(item: IErrorItem, showType?: boolean) {
    const {userId, docId, content, time, type} = item;

    const typeBgColor = ({
        [ErrorType.JS]: Colors.Red,
        [ErrorType.RESOURCE]: Colors.Purple,
        [ErrorType.API]: Colors.Yellow,
    } as const)[type];

    const renderType = {
        [ErrorType.JS]: '  js 异常',
        [ErrorType.RESOURCE]: 'res 异常',
        [ErrorType.API]: 'api 异常',
    }[type];

    return Paragraph(null, [
        ...(showType ? [Text(`[${renderType}]`, {bgColor: typeBgColor}), Text(' ')] : []),
        Text(`[${dayjs(time).format('MM/DD HH:mm:ss')}]`, {bgColor: Colors.Blue}),
        Text(' ', {bgColor: Colors.Blue}),
        Text(`[uid: ${userId}]`, {bgColor: Colors.Blue}),
        Text(' ', {bgColor: Colors.Blue}),
        Text(`[docId: ${docId}]`, {bgColor: Colors.Blue}),
        Text(' '),
        Text(`${content}`),
    ]);
}

function generateErrorChildren({title, list, showType, errorMsg}: {title: string, list: IErrorItem[], showType?: boolean, errorMsg?: string}) {
    let contentList = [];
    if (errorMsg) {
        contentList = [Paragraph(null, errorMsg)];
    } else {
        contentList = list.length > 0
            ? list.map(item => createErrorContent(item, showType))
            : [Paragraph(null, '无')];
    }

    return [Heading(3, null, title), ...contentList];
}

function generateContent(data: IContent) {
    const {queryUrls, errorMsgs, errorList, userAgentScope, filter: {startDate, endDate}} = data;

    const getQueryUrl = (type: ErrorType) => {
        return queryUrls.find(item => item.type === type)?.url || DefaultWeirwoodURL;
    };

    const userIds = Array.from(new Set(errorList.filter(item => item.userId).map(item => item.userId))).join('，');

    const baseBlock = Block(null, [
        Block.Item({bgColor: Colors.Blue}, [
            Heading(3, null, '基本信息'),
            Paragraph(null, `【影响用户userGuid】: ${userIds}`),
            Paragraph(null, `【时间范围】: ${formatTime(startDate, 'YYYY/MM/DD HH:mm:ss')} - ${formatTime(endDate, 'YYYY/MM/DD HH:mm:ss')}`),
            Paragraph(null, [
                Text('【weirwood地址】: '),
                Link({url: getQueryUrl(ErrorType.JS), title: 'JS异常'}),
                Text('，'),
                Link({url: getQueryUrl(ErrorType.API), title: 'API异常'}),
                Text('，'),
                Link({url: getQueryUrl(ErrorType.RESOURCE), title: '资源加载异常'}),
            ]),
            Paragraph(null, '【环境分布】: '),
            ...(userAgentScope ?? []).map((ua, index) => OrderedList(index, null, ua)),
        ]),
    ]);

    const jsBlock = Block(null, [
        Block.Item({bgColor: Colors.Red}, generateErrorChildren({
            title: 'JS异常',
            list: errorList.filter(item => item.type === ErrorType.JS),
            errorMsg: errorMsgs.find(item => item.type === ErrorType.JS)?.msg,
        })),
    ]);

    const apiAndResourceBlock = Block(null, [
        Block.Item({bgColor: Colors.Yellow}, generateErrorChildren({
            title: 'API异常',
            list: errorList.filter(item => item.type === ErrorType.API),
            errorMsg: errorMsgs.find(item => item.type === ErrorType.API)?.msg,
        })),
        Block.Item({bgColor: Colors.Yellow}, generateErrorChildren({
            title: '资源加载异常',
            list: errorList.filter(item => item.type === ErrorType.RESOURCE),
            errorMsg: errorMsgs.find(item => item.type === ErrorType.RESOURCE)?.msg,
        })),
    ]);

    const allErrorBlock = Block(null, [
        Block.Item({bgColor: Colors.Purple}, generateErrorChildren({
            title: '所有异常时间线排序',
            list: errorList,
            showType: true,
        })),
    ]);

    const conclusionBlock = Block(null, [
        Block.Item({bgColor: Colors.Gray}, [
            Heading(3, null, '处理结论'),
            Paragraph(null, [
                Text('请在此处手动填写，可在确认或疑似有问题的报错处进行划词评论', {color: Colors.Gray}),
            ]),
        ]),
    ]);

    const json = [
        baseBlock.toJson(),
        jsBlock.toJson(),
        apiAndResourceBlock.toJson(),
        allErrorBlock.toJson(),
        conclusionBlock.toJson(),
    ];

    return json;
}

function getDocIdFromUrl(url: string) {
    if (!url) {
        return '';
    }

    try {
        const {pathname} = new URL(url);
        if (pathname.startsWith('/knowledge') || pathname.startsWith('/m/knowledge')) {
            const segments = pathname.split('/');

            const baseNum = pathname.startsWith('/m/knowledge') ? 2 : 1;
            const docGuid = segments[baseNum + 4];
            return docGuid ?? '';
        }
        return '';
    } catch (e) {
        return '';
    }
}

export const Comp: React.FC<NodeViewRendererProps<IErrorLocate>> = (props) => {
    const {
        node,
        readonly,
        children,
    } = props;
    const savedFilter = node.filter || {};
    const isSaved = !!node.isSaved;

    const transform = useTransform();

    const [docId, setDocId] = useState(savedFilter.docId || '');
    const [userId, setUserId] = useState(savedFilter.userId || '');
    const [startDate, setStartDate] = useState(savedFilter.startDate ? dayjs(savedFilter.startDate) : dayjs().startOf('day'));
    const [endDate, setEndDate] = useState(savedFilter.endDate ? dayjs(savedFilter.endDate) : dayjs());
    const [isLoading, setIsLoading] = useState(false);

    const handleRun = async () => {
        if (!docId && !userId) {
            toast({type: ToastType.Warning, content: '文档 id 和用户 uuap 需至少填写一个', duration: 3000});
            return;
        }

        if (!startDate || !endDate) {
            toast({type: ToastType.Warning, content: '时间范围不能为空', duration: 3000});
            return;
        }

        transform.commands.replaceNodes(node, Paragraph(null, '').toJson(), {replaceChildren: true});
        setIsLoading(true);

        try {
            const result = await sdk.serverBase.request({
                method: 'post',
                url: 'https://prod--soqa-rldoc-server--test-tool.appspace.baidu.com/openapi/weirwood/error/list',
                timeout: 60000,
                data: {
                    uuap: userId || undefined,
                    doc_id: docId || undefined,
                    startDate: startDate ? startDate.valueOf() : undefined,
                    endDate: endDate ? endDate.valueOf() : undefined,
                },
            });

            // const result = await mockRequest();

            if (Array.isArray(result)) {
                const errorList: IErrorItem[] = [];
                const queryUrls: IQueryUrlItem[] = [];
                const errorMsgs: IErrorMsg[] = [];
                const userAgentScopeSet = new Set<string>();

                result.forEach(r => {
                    const type = r.error_type;
                    queryUrls.push({
                        type,
                        url: r.url_link || '',
                    });
                    errorMsgs.push({
                        type,
                        msg: r.error_message || '',
                    });

                    if (ErrorTypeList.includes(type) && Array.isArray(r.errors)) {
                        r.errors.forEach((error: any) => {
                            let content = '';
                            const errorUserIds = (error?.user_guid_list || []).join('，');
                            const errorDocIds = (error?.doc_guid_list || []).map((url: string) => getDocIdFromUrl(url)).join('，');

                            if (type === 'js') {
                                content = error.errorMessage;
                            } else if (type === 'resource') {
                                content = error.element;
                            } else if (type === 'api') {
                                content = error.fingerprint || error.url;
                            }

                            errorList.push({
                                userId: errorUserIds,
                                docId: errorDocIds,
                                type,
                                content: content.substring(0, 100),
                                time: dayjs(error.lastSeen).valueOf(),
                            });

                            if (Array.isArray(error.userDeviceList)) {
                                error.userDeviceList.forEach((ua: string) => {
                                    userAgentScopeSet.add(ua);
                                });
                            }
                        });
                    }
                });

                const json = generateContent({
                    queryUrls,
                    errorMsgs,
                    errorList: errorList.sort((a, b) => b.time - a.time),
                    userAgentScope: Array.from(userAgentScopeSet),
                    filter: {
                        userId,
                        docId,
                        startDate: startDate.valueOf(),
                        endDate: endDate.valueOf(),
                    },
                });

                transform.commands.replaceNodes(node, json, {replaceChildren: true});

                setIsLoading(false);
            }
        } catch (e) {
            console.error(e);
            setIsLoading(false);
            transform.commands.replaceNodes(node, Paragraph(null, '获取错误日志失败，请重试').toJson(), {replaceChildren: true});
        }
    };

    const handleSave = () => {
        transform.commands.setNode(node, {
            isSaved: true,
            filter: {
                docId,
                userId,
                startDate: startDate ? startDate.valueOf() : undefined,
                endDate: endDate ? endDate.valueOf() : undefined,
            },
        });
    };

    const isReadonly = readonly || isSaved;

    return (
        <div className={styles.container}>
            {isLoading && <Loading className={styles.loading} visible message={'获取数据中…'} />}
            <div className={styles.header} contentEditable={false}>
                <Input
                    disabled={isReadonly}
                    prefix="文档 id："
                    placeholder="请输入"
                    value={docId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocId(e.target.value)}
                    onPressEnter={handleRun}
                />
                <Input
                    disabled={isReadonly}
                    prefix="用户 uuap："
                    placeholder="请输入"
                    value={userId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserId(e.target.value)}
                    onPressEnter={handleRun}
                />
                <RangePicker
                    size={'md'}
                    className={styles.rangePicker}
                    disabled={isReadonly}
                    mode={'dateTime'}
                    itemFormat={'YYYY年MM月DD日 HH:mm:ss'}
                    startValue={startDate}
                    endValue={endDate}
                    onChange={(start, end) => {
                        console.log('选择的日期：', start, end);
                        setStartDate(start!);
                        setEndDate(end!);
                    }}
                />
                {!isReadonly && (
                    <>
                        <Button type={'important'} onClick={handleRun}>Run</Button>
                        <Button type={'important'} onClick={handleSave}>Save</Button>
                    </>
                )}

            </div>
            <div className={styles.body}>
                {children}
            </div>
        </div>
    );
};

export const InlineComp: React.FC<NodeViewRendererProps<IErrorLocate>> = (props) => {
    const {node, children} = props;

    const savedFilter = node.filter || {};
    const docId = savedFilter.docId || '';
    const userId = savedFilter.userId || '';
    const startDate = savedFilter.startDate ? dayjs(savedFilter.startDate) : dayjs().startOf('day');
    const endDate = savedFilter.endDate ? dayjs(savedFilter.endDate) : dayjs();

    return (
        <div
            style={{
                border: '0.0625em solid #d9d9d9',
                borderRadius: '0.5em',
                overflow: 'hidden',
                boxShadow: '0 0.25em 0.625em rgba(0, 0, 0, 0.1)',
                position: 'relative',
            }}
        >
            <div
                contentEditable={false}
                style={{
                    backgroundColor: '#f9f9f9',
                    padding: '0.75em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625em',
                    borderBottom: '0.0625em solid #eee',
                }}
            >
                <Input
                    disabled
                    value={docId}
                />
                <Input
                    disabled
                    value={userId}
                />
                <RangePicker
                    size={'md'}
                    disabled
                    mode={'dateTime'}
                    itemFormat={'YYYY年MM月DD日 HH:mm:ss'}
                    startValue={startDate}
                    endValue={endDate}
                />
            </div>
            <div
                style={{
                    padding: '1em',
                    minHeight: '9.375em',
                    backgroundColor: '#fff',
                }}
            >
                {children}
            </div>
        </div>
    );
};
